/**
 * CR-8613: what each colour in the New Call Off warehouse grid means.
 *
 * The key above the grid and the tooltip on each coloured row come from here,
 * so they cannot drift from each other. The classes are the ones
 * WarehouseDeliveryGrid paints with (`getStockHealthColor`, the Qty cell and
 * `PRIORITY_ROW_CLASS` / `PRIORITY_EDGE_CLASS`).
 */
import type { StockPlanningItem } from '../../../lib/api';
import type { ColourKeyEntry } from '../shared/ColourKey';
import DirectProductStar from '../shared/DirectProductStar';
import { getStockHealthColor } from './warehouseWizardGrid';

/** The warehouse grid's colour key, in the order the ticket lists them. */
export const WAREHOUSE_GRID_COLOUR_KEY: readonly ColourKeyEntry[] = [
  { swatchClassName: 'bg-red-50', label: 'Pink: Stock Health Min or Max below 0' },
  { swatchClassName: 'bg-yellow-50', label: 'Yellow row: Stock Health Min or Max below 50' },
  { swatchClassName: 'bg-blue-50', label: 'Blue: a quantity has been entered' },
  {
    swatchClassName: 'bg-orange-100 shadow-[inset_3px_0_0_0_#f97316]',
    label: 'Orange: priority on this call-off',
  },
  { swatchClassName: 'bg-yellow-100', label: 'Yellow Qty column: type the quantity here' },
  { swatchClassName: 'bg-red-50 border-red-500', label: 'Red Qty: more than is available' },
  { swatchClassName: '', label: 'Red figure: negative Stock Health', icon: (
    <span aria-hidden="true" className="font-semibold text-red-600">-5</span>
  ) },
  { swatchClassName: '', label: 'Direct product', icon: <DirectProductStar /> },
];

/**
 * Which Stock Health values are low, in words.
 *
 * @param min - Stock Health Min
 * @param max - Stock Health Max
 * @returns e.g. "Stock Health Min below 0", or null when neither is low
 */
export function stockHealthReason(min: string, max: string): string | null {
  const minVal = parseFloat(min) || 0;
  const maxVal = parseFloat(max) || 0;
  const below = (limit: number) => [
    minVal < limit ? 'Min' : '', maxVal < limit ? 'Max' : '',
  ].filter(Boolean).join(' and ');
  const negative = below(0);
  if (negative) return `Stock Health ${negative} below 0`;
  const low = below(50);
  return low ? `Stock Health ${low} below 50` : null;
}

/**
 * The tooltip of a coloured row: why it is that colour, fill first.
 *
 * Mirrors WarehouseDeliveryGrid's `rowClass`: a priority row keeps its Stock
 * Health tint (marked by the orange edge) or is orange; any other row is blue
 * once it has a quantity, else its Stock Health tint.
 *
 * @param item - the grid row
 * @param priority - whether the row is ticked Priority
 * @param qty - the quantity entered on it
 * @returns the tooltip, or undefined for an uncoloured row
 */
export function warehouseRowTitle(
  item: StockPlanningItem, priority: boolean, qty: number,
): string | undefined {
  const health = stockHealthReason(item.stock_health_min, item.stock_health_max);
  const tint = getStockHealthColor(item.stock_health_min, item.stock_health_max);
  const tintName = tint === 'bg-red-50' ? 'Pink' : 'Yellow';
  const reasons: string[] = [];
  if (priority) {
    if (health) reasons.push(`${tintName}: ${health}`, 'Orange edge: priority on this call-off');
    else reasons.push('Orange: priority on this call-off');
  } else if (qty > 0) {
    reasons.push('Blue: a quantity has been entered');
    if (health) reasons.push(health);
  } else if (health) {
    reasons.push(`${tintName}: ${health}`);
  }
  return reasons.length > 0 ? reasons.join(' · ') : undefined;
}
